// engagement-components/TemplateManager.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaWhatsapp,
  FaSms,
  FaEnvelope,
  FaLink,
  FaArrowRight,
  FaEye,
  FaFileAlt,
  FaPoll,
  FaGlobe,
  FaTimes,
  FaSearch,
  FaSyncAlt,
  FaFilter,
  FaImage,
  FaUpload,
  FaSave,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaListOl,
  FaHeading,
  FaQuoteLeft,
  FaCode,
  FaEraser,
} from "react-icons/fa";
import CryptoJS from "crypto-js";
import axios from "axios";
import { apiUrl, authHeaders, uploadHeaders } from "./config";

const surveyBaseUrl =
  process.env.REACT_APP_SURVEY_BASE_URL || "https://secure.netshineoffice.com";

const generateSurveyLink = (surveyId) => {
  const companyId = localStorage.getItem("companyid");
  const encryptedOrgId = companyId
    ? encodeURIComponent(CryptoJS.AES.encrypt(companyId, "ns0-200200221").toString())
    : "your-restaurant-id";
  const key = encodeURIComponent(`${encryptedOrgId}-${surveyId}`);
  return `${surveyBaseUrl}/feedback.html?${key}`;
};

const CHANNELS = ["WhatsApp", "SMS", "Email"];

const VARIABLES = [
  { v: "{{guest_name}}", desc: "Guest's full name" },
  { v: "{{hotel_name}}", desc: "Property name" },
  { v: "{{checkin_date}}", desc: "Check-in date" },
  { v: "{{checkout_date}}", desc: "Check-out date" },
  { v: "{{room_number}}", desc: "Room number" },
  { v: "{{link}}", desc: "Tracked link — landing page, feedback form, or custom URL" },
];

const CHANNEL_META = {
  WhatsApp: { icon: <FaWhatsapp />, color: "#25d366", bg: "#f0fdf4", label: "WhatsApp" },
  SMS: { icon: <FaSms />, color: "#6c757d", bg: "#f9fafb", label: "SMS" },
  Email: { icon: <FaEnvelope />, color: "#0d6efd", bg: "#eff6ff", label: "Email" },
};

const LINK_TYPES = [
  {
    id: "landing",
    label: "Offer Page",
    icon: <FaFileAlt />,
    color: "#0d6efd",
    desc: "Link to a landing page with images, pricing, and CTA buttons.",
  },
  {
    id: "feedback",
    label: "Feedback Form",
    icon: <FaPoll />,
    color: "#198754",
    desc: "Link to an existing feedback survey.",
  },
  {
    id: "custom",
    label: "Custom URL",
    icon: <FaGlobe />,
    color: "#6c757d",
    desc: "Use a booking engine, website, social link, or any external URL.",
  },
  {
    id: "none",
    label: "No Link",
    icon: <FaTimes />,
    color: "#94a3b8",
    desc: "Send the message without a tracked link.",
  },
];

const emptyTemplate = () => ({
  id: null,
  name: "",
  channel: "WhatsApp",
  subject: "",
  content: "",
  linkType: "none",
  landingPageId: "",
  surveyId: "",
  customUrl: "",
  headerImage: "",
  status: "Active",
});

const normalizeTemplate = (t = {}) => ({
  ...t,
  id: t.templateid || t.id,
  subject: t.subject || "",
  linkType: t.link_type || t.linkType || "none",
  landingPageId: t.landing_page_id || t.landingPageId || "",
  surveyId: t.survey_id || t.surveyId || "",
  customUrl: t.custom_url || t.customUrl || "",
  headerImage: t.header_image || t.headerImage || "",
  status: t.status || "Active",
});

const resolveLink = (template, landingPages, surveys) => {
  if (!template) return null;

  switch (template.linkType) {
    case "landing": {
      const lp = landingPages.find((x) => String(x.id) === String(template.landingPageId));
      return lp ? { label: lp.title, url: `/o/${lp.slug}`, type: "landing", obj: lp } : null;
    }

    case "feedback": {
      const sv = surveys.find((x) => String(x.surveyid) === String(template.surveyId));
      return sv
        ? {
            label: sv.title,
            url: generateSurveyLink(sv.surveyid),
            type: "feedback",
            obj: sv,
          }
        : null;
    }

    case "custom":
      return template.customUrl
        ? {
            label: template.customUrl,
            url: template.customUrl,
            type: "custom",
            obj: null,
          }
        : null;

    default:
      return null;
  }
};

const getLinkLabel = (linkType) => {
  const item = LINK_TYPES.find((lt) => lt.id === linkType);
  return item?.label || "No Link";
};

const replacePreviewVariables = (content, displayUrl) =>
  (content || "")
    .replaceAll("{{guest_name}}", "Rahul Sharma")
    .replaceAll("{{hotel_name}}", "Grand Netshine Hotel")
    .replaceAll("{{checkin_date}}", "15 Jan 2025")
    .replaceAll("{{checkout_date}}", "17 Jan 2025")
    .replaceAll("{{room_number}}", "204")
    .replaceAll("{{link}}", displayUrl || "hotel.co/link");

const EMAIL_ALLOWED_TAGS = new Set([
  "A",
  "B",
  "BR",
  "BLOCKQUOTE",
  "DIV",
  "EM",
  "H1",
  "H2",
  "H3",
  "H4",
  "HR",
  "I",
  "IMG",
  "LI",
  "OL",
  "P",
  "SPAN",
  "STRONG",
  "TABLE",
  "TBODY",
  "TD",
  "TH",
  "THEAD",
  "TR",
  "U",
  "UL",
]);

const EMAIL_ALLOWED_ATTRS = new Set([
  "align",
  "alt",
  "border",
  "cellpadding",
  "cellspacing",
  "colspan",
  "height",
  "href",
  "rel",
  "rowspan",
  "src",
  "style",
  "target",
  "title",
  "width",
]);

const escapeHtml = (value = "") =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const isLikelyHtml = (value = "") => /<\/?[a-z][\s\S]*>/i.test(value || "");

const plainTextToEmailHtml = (value = "") => {
  const safe = escapeHtml(value || "").replace(/\r\n|\r|\n/g, "<br />");
  return safe ? `<p>${safe}</p>` : "";
};

const htmlToPlainText = (html = "") => {
  if (!html) return "";

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return String(html)
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>|<\/div>|<\/li>|<\/h[1-6]>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
  }

  const doc = new DOMParser().parseFromString(String(html), "text/html");
  return (doc.body?.innerText || "").trim();
};

const hasTemplateContent = (content = "") =>
  htmlToPlainText(content).replace(/\u00a0/g, " ").trim().length > 0 || String(content || "").includes("{{link}}");

const getTemplateSummary = (template = {}) =>
  template.channel === "Email" ? htmlToPlainText(template.content || "") : template.content || "";

const getContentLength = (templateOrContent, maybeChannel) => {
  const content =
    typeof templateOrContent === "object" && templateOrContent !== null
      ? templateOrContent.content || ""
      : templateOrContent || "";
  const channel =
    typeof templateOrContent === "object" && templateOrContent !== null
      ? templateOrContent.channel
      : maybeChannel;

  return channel === "Email" ? htmlToPlainText(content).length : String(content || "").length;
};

const sanitizeInlineStyle = (style = "") =>
  String(style)
    .split(";")
    .map((rule) => rule.trim())
    .filter((rule) => {
      if (!rule || !rule.includes(":")) return false;
      const lower = rule.toLowerCase();
      return !lower.includes("expression") && !lower.includes("javascript:") && !lower.includes("url(");
    })
    .join("; ");

const isSafeTemplateUrl = (url = "") => {
  const value = String(url || "").trim();

  if (!value) return false;
  if (value.includes("{{link}}")) return true;
  if (value.startsWith("#") || value.startsWith("/") || value.startsWith("./") || value.startsWith("../")) return true;

  return /^(https?:|mailto:|tel:)/i.test(value);
};

const sanitizeEmailHtml = (html = "") => {
  if (!html) return "";
  const value = isLikelyHtml(html) ? String(html) : plainTextToEmailHtml(html);

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return value
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/\son\w+="[^"]*"/gi, "")
      .replace(/\son\w+='[^']*'/gi, "");
  }

  const doc = new DOMParser().parseFromString(value, "text/html");

  doc.body.querySelectorAll("*").forEach((node) => {
    if (!EMAIL_ALLOWED_TAGS.has(node.tagName)) {
      node.replaceWith(...Array.from(node.childNodes));
      return;
    }

    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const attrValue = attr.value || "";

      if (name.startsWith("on") || !EMAIL_ALLOWED_ATTRS.has(attr.name.toLowerCase())) {
        node.removeAttribute(attr.name);
        return;
      }

      if ((name === "href" || name === "src") && !isSafeTemplateUrl(attrValue)) {
        node.removeAttribute(attr.name);
        return;
      }

      if (name === "style") {
        const cleanStyle = sanitizeInlineStyle(attrValue);

        if (cleanStyle) {
          node.setAttribute("style", cleanStyle);
        } else {
          node.removeAttribute("style");
        }
      }
    });

    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noreferrer");
    }
  });

  return doc.body.innerHTML;
};

const normalizeContentForChannel = (content = "", channel = "WhatsApp") => {
  if (channel === "Email") return sanitizeEmailHtml(content);
  return htmlToPlainText(content);
};

const EmailHtmlEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [sourceMode, setSourceMode] = useState(false);

  useEffect(() => {
    if (sourceMode || !editorRef.current) return;

    const nextHtml = value ? (isLikelyHtml(value) ? value : plainTextToEmailHtml(value)) : "";

    if (editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
  }, [sourceMode, value]);

  const emitChange = useCallback(() => {
    if (!editorRef.current) return;
    onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const runCommand = (command, commandValue = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const insertHtml = (html) => {
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, html);
    emitChange();
  };

  const createLink = () => {
    const href = window.prompt("Enter URL or use {{link}}", "{{link}}");

    if (!href) return;

    runCommand("createLink", href);
  };

  const handlePaste = (event) => {
    event.preventDefault();

    const html = event.clipboardData.getData("text/html");
    const text = event.clipboardData.getData("text/plain");
    const clean = html ? sanitizeEmailHtml(html) : plainTextToEmailHtml(text);

    document.execCommand("insertHTML", false, clean);
    emitChange();
  };

  if (sourceMode) {
    return (
      <div className="eng-template-html-editor">
        <div className="eng-template-html-toolbar">
          <button type="button" className="is-active" onClick={() => setSourceMode(false)}>
            <FaCode /> Visual Editor
          </button>
        </div>

        <textarea
          className="eng-template-html-source"
          rows={11}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder="<p>Hi {{guest_name}},</p>"
        />
      </div>
    );
  }

  return (
    <div className="eng-template-html-editor">
      <div className="eng-template-html-toolbar" aria-label="Email HTML editor toolbar">
        <button type="button" title="Heading" onClick={() => runCommand("formatBlock", "H2")}>
          <FaHeading />
        </button>
        <button type="button" title="Bold" onClick={() => runCommand("bold")}>
          <FaBold />
        </button>
        <button type="button" title="Italic" onClick={() => runCommand("italic")}>
          <FaItalic />
        </button>
        <button type="button" title="Underline" onClick={() => runCommand("underline")}>
          <FaUnderline />
        </button>
        <button type="button" title="Bulleted list" onClick={() => runCommand("insertUnorderedList")}>
          <FaListUl />
        </button>
        <button type="button" title="Numbered list" onClick={() => runCommand("insertOrderedList")}>
          <FaListOl />
        </button>
        <button type="button" title="Quote" onClick={() => runCommand("formatBlock", "BLOCKQUOTE")}>
          <FaQuoteLeft />
        </button>
        <button type="button" title="Create link" onClick={createLink}>
          <FaLink />
        </button>
        <button
          type="button"
          title="Insert CTA button"
          onClick={() =>
            insertHtml(
              '<p><a href="{{link}}" style="display:inline-block;background:#4f46e5;color:#ffffff;text-decoration:none;padding:12px 18px;border-radius:12px;font-weight:700;">Open Link</a></p>'
            )
          }
        >
          CTA
        </button>
        <button type="button" title="Clear formatting" onClick={() => runCommand("removeFormat")}>
          <FaEraser />
        </button>
        <button type="button" title="HTML source" onClick={() => setSourceMode(true)}>
          <FaCode />
        </button>
      </div>

      <div
        ref={editorRef}
        className="eng-template-html-surface"
        contentEditable
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={handlePaste}
        suppressContentEditableWarning
      />
    </div>
  );
};

const MessagePreview = ({ template, link }) => {
  const displayUrl = link
    ? link.type === "feedback"
      ? "hotel.co/feedback?key=..."
      : link.type === "landing"
      ? `hotel.co/o/${link.obj?.slug || "offer"}`
      : link.url
    : null;

  const msg = replacePreviewVariables(template?.content || "", displayUrl);
  const empty = !hasTemplateContent(template?.content || "");
  const ch = template?.channel || "WhatsApp";

  if (ch === "WhatsApp") {
    return (
      <div className="eng-template-preview-whatsapp">
        {empty ? (
          <div className="eng-template-preview-empty">
            <FaWhatsapp />
            <small>Start typing to see WhatsApp preview</small>
          </div>
        ) : (
          <div className="eng-template-whatsapp-bubble">
            {template?.headerImage && (
              <img
                className="eng-template-whatsapp-image"
                src={template.headerImage}
                alt="Header"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            )}

            <div className="eng-template-preview-text">{htmlToPlainText(msg)}</div>

            {link && (
              <div className="eng-template-preview-link">
                {link.type === "feedback" && <FaPoll />}
                {link.type === "landing" && <FaFileAlt />}
                {link.type === "custom" && <FaGlobe />}
                <span>{displayUrl}</span>
                <em>→ {link.label}</em>
              </div>
            )}

            <div className="eng-template-preview-time">09:00 ✓✓</div>
          </div>
        )}
      </div>
    );
  }

  if (ch === "SMS") {
    return (
      <div className="eng-template-preview-sms">
        {empty ? (
          <span className="eng-template-muted">SMS preview...</span>
        ) : (
          <div className="eng-template-preview-text">{htmlToPlainText(msg)}</div>
        )}

        {!empty && link && <div className="eng-template-sms-link">{displayUrl}</div>}
      </div>
    );
  }

  return (
    <div className="eng-template-preview-email">
      <div className="eng-template-email-head">
        <span>From: hotel@netshine.com</span>
        <strong>{template?.subject || "Your subject will appear here"}</strong>
      </div>

      <div className="eng-template-email-body">
        {empty ? (
          <span className="eng-template-muted">Email preview...</span>
        ) : (
          <div
            className="eng-template-email-html"
            dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(msg) }}
          />
        )}

        {!empty && link && !msg.includes('href="{{link}}"') && (
          <div className="eng-template-email-link">
            {displayUrl} — {link.label}
          </div>
        )}
      </div>
    </div>
  );
};

const TemplateManager = ({ onNavigate }) => {
  const [templates, setTemplates] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmData, setConfirmData] = useState(null);

  const [filterCh, setFilterCh] = useState("All");
  const [filterLink, setFilterLink] = useState("All");
  const [searchText, setSearchText] = useState("");

  const fetchAll = useCallback(async (manualRefresh = false) => {
    if (manualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [tRes, lpRes, svRes] = await Promise.all([
        axios.get(`${apiUrl}/feedback/templates`, authHeaders()),
        axios.get(`${apiUrl}/feedback/landing-pages`, authHeaders()),
        axios.get(`${apiUrl}/feedback/surveys`, authHeaders()),
      ]);

      setTemplates((tRes.data || []).map(normalizeTemplate));

      setLandingPages(
        (lpRes.data || []).map((p) => ({
          ...p,
          id: p.pageid || p.id,
        }))
      );

      const rawSurveys = Array.isArray(svRes.data) ? svRes.data : [];
      setSurveys(
        rawSurveys.map((s) => ({
          surveyid: s.surveyid || s.id,
          title: s.title || "Untitled Survey",
          status: s.status || "Draft",
          responses: s.total_responses || s.responses || 0,
        }))
      );
    } catch (e) {
      console.error("Error fetching templates:", e);
      setTemplates([]);
      setLandingPages([]);
      setSurveys([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(false);
  }, [fetchAll]);

  useEffect(() => {
    if (!showEditor && !confirmData) return undefined;

    document.body.classList.add("eng-template-modal-open");

    return () => {
      document.body.classList.remove("eng-template-modal-open");
    };
  }, [showEditor, confirmData]);

  const openCreate = () => {
    setEditing(emptyTemplate());
    setShowEditor(true);
    setError("");
    setSuccess("");
  };

  const openEdit = (t) => {
    const normalized = {
      ...emptyTemplate(),
      ...normalizeTemplate(t),
    };

    if (normalized.channel === "Email" && normalized.content && !isLikelyHtml(normalized.content)) {
      normalized.content = plainTextToEmailHtml(normalized.content);
    }

    setEditing(normalized);
    setShowEditor(true);
    setError("");
    setSuccess("");
  };

  const closeEditor = () => {
    if (saving || uploadingImage) return;
    setShowEditor(false);
    setEditing(null);
    setError("");
  };

  const insertVariable = (v) => {
    setEditing((p) => ({
      ...p,
      content: p.channel === "Email" ? `${p.content || "<p></p>"} ${v}` : `${p.content || ""}${v}`,
    }));
  };

  const updateEditing = (key, value) => {
    setEditing((previous) => ({ ...previous, [key]: value }));
    if (error) setError("");
  };

  const handleChannelChange = (channel) => {
    setEditing((previous) => {
      const currentChannel = previous.channel;
      let nextContent = previous.content || "";

      if (channel === "Email" && currentChannel !== "Email") {
        nextContent = plainTextToEmailHtml(nextContent);
      }

      if (channel !== "Email" && currentChannel === "Email") {
        nextContent = htmlToPlainText(nextContent);
      }

      return {
        ...previous,
        channel,
        content: nextContent,
        subject: channel === "Email" ? previous.subject : "",
        headerImage: channel === "WhatsApp" ? previous.headerImage : "",
      };
    });

    if (error) setError("");
  };

  const save = async () => {
    if (!editing?.name?.trim()) {
      setError("Template name is required.");
      return;
    }

    if (!hasTemplateContent(editing?.content || "")) {
      setError("Message content is required.");
      return;
    }

    if (editing.linkType !== "none" && !editing.content.includes("{{link}}")) {
      setError("You selected a link type but {{link}} is missing from your message. Add {{link}} where you want the URL to appear.");
      return;
    }

    if (editing.channel === "Email" && !editing.subject?.trim()) {
      setError("Email subject is required for Email templates.");
      return;
    }

    if (editing.linkType === "landing" && !editing.landingPageId) {
      setError("Select an offer page for the {{link}} destination.");
      return;
    }

    if (editing.linkType === "feedback" && !editing.surveyId) {
      setError("Select a feedback survey for the {{link}} destination.");
      return;
    }

    if (editing.linkType === "custom" && !editing.customUrl?.trim()) {
      setError("Enter a custom URL for the {{link}} destination.");
      return;
    }

    setSaving(true);
    setError("");

    const contentForSave = normalizeContentForChannel(editing.content, editing.channel);
    const normalizedEditing = { ...editing, content: contentForSave };

    const payload = {
      name: editing.name,
      channel: editing.channel,
      subject: editing.channel === "Email" ? editing.subject || "" : "",
      content: contentForSave,
      link_type: editing.linkType,
      landing_page_id: editing.landingPageId || null,
      survey_id: editing.surveyId || null,
      custom_url: editing.customUrl || null,
      header_image: editing.headerImage || null,
      status: editing.status || "Active",
    };

    const id = editing.templateid || editing.id;

    try {
      if (id) {
        await axios.put(`${apiUrl}/feedback/templates/${id}`, payload, authHeaders());

        setTemplates((prev) =>
          prev.map((t) => ((t.templateid || t.id) === id ? normalizeTemplate({ ...t, ...normalizedEditing, id }) : t))
        );
      } else {
        const res = await axios.post(`${apiUrl}/feedback/templates`, payload, authHeaders());

        const saved = res.data
          ? normalizeTemplate({
              ...res.data,
              id: res.data.templateid || res.data.id,
              linkType: res.data.link_type || res.data.linkType || normalizedEditing.linkType,
              landingPageId: res.data.landing_page_id || res.data.landingPageId || normalizedEditing.landingPageId,
              surveyId: res.data.survey_id || res.data.surveyId || normalizedEditing.surveyId,
              customUrl: res.data.custom_url || res.data.customUrl || normalizedEditing.customUrl,
              headerImage: res.data.header_image || res.data.headerImage || normalizedEditing.headerImage,
              subject: res.data.subject || normalizedEditing.subject,
              content: res.data.content || normalizedEditing.content,
              channel: res.data.channel || normalizedEditing.channel,
            })
          : normalizeTemplate({ ...normalizedEditing, id: `t${Date.now()}` });

        setTemplates((prev) => [saved, ...prev]);
      }

      setSuccess("Template saved successfully.");
      setShowEditor(false);
      setEditing(null);
    } catch (e) {
      console.error("Error saving template:", e);

      const saved = normalizeTemplate({ ...normalizedEditing, id: id || `t${Date.now()}` });

      setTemplates((prev) =>
        id ? prev.map((t) => ((t.templateid || t.id) === id ? saved : t)) : [saved, ...prev]
      );

      setSuccess("Template saved locally. Please verify server sync.");
      setShowEditor(false);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async () => {
    const id = confirmData?.templateid || confirmData?.id;

    if (!id) return;

    try {
      await axios.delete(`${apiUrl}/feedback/templates/${id}`, authHeaders());
    } catch (e) {
      console.error("Error deleting template:", e);
    }

    setTemplates((prev) => prev.filter((t) => (t.templateid || t.id) !== id));
    setConfirmData(null);
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError("Image upload failed. Maximum file size is 2 MB.");
      event.target.value = "";
      return;
    }

    setUploadingImage(true);
    setError("");

    try {
      const fd = new FormData();
      fd.append("image", file);

      const res = await axios.post(`${apiUrl}/feedback/templates/upload-image`, fd, uploadHeaders());

      if (res.data?.url) {
        updateEditing("headerImage", res.data.url);
      } else {
        setError("Image uploaded, but server did not return a URL.");
      }
    } catch (e) {
      console.error("Error uploading template image:", e);
      setError("Image upload failed. Check file size and try again.");
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return templates.filter((t) => {
      const matchesSearch =
        !q ||
        `${t.name || ""} ${t.channel || ""} ${t.subject || ""} ${getTemplateSummary(t)} ${getLinkLabel(t.linkType)}`
          .toLowerCase()
          .includes(q);

      const matchesChannel = filterCh === "All" || t.channel === filterCh;
      const matchesLink = filterLink === "All" || t.linkType === filterLink;

      return matchesSearch && matchesChannel && matchesLink;
    });
  }, [templates, searchText, filterCh, filterLink]);

  const stats = useMemo(() => {
    const whatsapp = templates.filter((t) => t.channel === "WhatsApp").length;
    const sms = templates.filter((t) => t.channel === "SMS").length;
    const email = templates.filter((t) => t.channel === "Email").length;
    const linked = templates.filter((t) => t.linkType && t.linkType !== "none").length;

    return {
      total: templates.length,
      whatsapp,
      sms,
      email,
      linked,
    };
  }, [templates]);

  const resolvedLink = resolveLink(editing, landingPages, surveys);
  const hasLinkInMsg = editing?.content?.includes("{{link}}");

  const renderCardLinkBadge = (t) => {
    const link = resolveLink(t, landingPages, surveys);

    if (link?.type === "feedback") {
      return (
        <div className="eng-template-link-badge feedback">
          <FaPoll />
          <span>
            <strong>{link.label}</strong>
            <em>Feedback survey</em>
          </span>
        </div>
      );
    }

    if (link?.type === "landing") {
      return (
        <div className="eng-template-link-badge landing">
          <FaFileAlt />
          <span>
            <strong>{link.label}</strong>
            <em>Offer page</em>
          </span>
        </div>
      );
    }

    if (link?.type === "custom") {
      return (
        <div className="eng-template-link-badge custom">
          <FaGlobe />
          <span>
            <strong>{link.url}</strong>
            <em>Custom URL</em>
          </span>
        </div>
      );
    }

    if (t.content?.includes("{{link}}") && t.linkType === "none") {
      return (
        <div className="eng-template-link-badge warning">
          <FaLink />
          <span>
            <strong>{"{{link}}"} used — no destination set</strong>
            <em>Attach a destination before using this template</em>
          </span>
          <button type="button" onClick={() => openEdit(t)}>
            Attach →
          </button>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="eng-template-root">
        <style>{templateStyles}</style>
        <div className="eng-template-loader">
          <div className="eng-template-dots">
            <span />
            <span />
            <span />
            <span />
          </div>
          <strong>Loading templates...</strong>
          <p>Preparing message templates, landing pages, and feedback surveys.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eng-template-root">
      <style>{templateStyles}</style>

      <section className="eng-template-hero">
        <div className="eng-template-hero-left">
          <span className="eng-template-hero-icon">
            <FaFileAlt />
          </span>

          <div>
            <div className="eng-template-eyebrow">Guest Messaging Library</div>
            <h2>Templates</h2>
            <p>Build reusable WhatsApp, SMS, and Email templates with tracked links to offers, surveys, or custom URLs.</p>
          </div>
        </div>

        <div className="eng-template-hero-actions">
          <button
            type="button"
            className="eng-template-soft-btn"
            onClick={() => fetchAll(true)}
            disabled={refreshing}
          >
            <FaSyncAlt className={refreshing ? "eng-spin" : ""} />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>

          <button type="button" className="eng-template-primary-btn" onClick={openCreate}>
            <FaPlus />
            New Template
          </button>
        </div>
      </section>

      {success && (
        <div className="eng-template-alert success">
          <FaCheckCircle />
          <span>{success}</span>
          <button type="button" onClick={() => setSuccess("")}>
            <FaTimes />
          </button>
        </div>
      )}

      <section className="eng-template-stat-grid">
        <article className="eng-template-stat" style={{ "--tone": "#4f46e5" }}>
          <span>Total Templates</span>
          <strong>{stats.total}</strong>
        </article>
        <article className="eng-template-stat" style={{ "--tone": "#25d366" }}>
          <span>WhatsApp</span>
          <strong>{stats.whatsapp}</strong>
        </article>
        <article className="eng-template-stat" style={{ "--tone": "#6c757d" }}>
          <span>SMS</span>
          <strong>{stats.sms}</strong>
        </article>
        <article className="eng-template-stat" style={{ "--tone": "#0d6efd" }}>
          <span>Email</span>
          <strong>{stats.email}</strong>
        </article>
        <article className="eng-template-stat" style={{ "--tone": "#059669" }}>
          <span>Linked</span>
          <strong>{stats.linked}</strong>
        </article>
      </section>

      <section className="eng-template-tip">
        <FaInfoCircle />
        <span>
          Add <code>{"{{link}}"}</code> to your message, then choose where it opens: a feedback survey,
          an offer page, or any custom URL. Every tap can be tracked per guest.
        </span>
      </section>

      <section className="eng-template-toolbar">
        <div className="eng-template-search">
          <FaSearch />
          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search template name, channel, content, or link type..."
          />
        </div>

        <div className="eng-template-filter-group">
          <span className="eng-template-filter-label">
            <FaFilter />
            Filters
          </span>

          <select value={filterCh} onChange={(event) => setFilterCh(event.target.value)}>
            {["All", ...CHANNELS].map((ch) => (
              <option key={ch} value={ch}>
                {ch === "All" ? "All Channels" : ch}
              </option>
            ))}
          </select>

          <select value={filterLink} onChange={(event) => setFilterLink(event.target.value)}>
            {["All", ...LINK_TYPES.map((lt) => lt.id)].map((item) => (
              <option key={item} value={item}>
                {item === "All" ? "All Links" : getLinkLabel(item)}
              </option>
            ))}
          </select>

          {(searchText || filterCh !== "All" || filterLink !== "All") && (
            <button
              type="button"
              className="eng-template-clear-btn"
              onClick={() => {
                setSearchText("");
                setFilterCh("All");
                setFilterLink("All");
              }}
            >
              Clear
            </button>
          )}
        </div>
      </section>

      {templates.length === 0 ? (
        <section className="eng-template-empty">
          <span className="eng-template-empty-icon">
            <FaFileAlt />
          </span>
          <strong>No templates yet</strong>
          <p>Create your first reusable message template for WhatsApp, SMS, or Email campaigns.</p>
          <button type="button" className="eng-template-primary-btn" onClick={openCreate}>
            <FaPlus />
            Create First Template
          </button>
        </section>
      ) : filtered.length === 0 ? (
        <section className="eng-template-empty">
          <span className="eng-template-empty-icon">
            <FaSearch />
          </span>
          <strong>No templates match the filters</strong>
          <p>Adjust search, channel, or link-type filters to view more templates.</p>
        </section>
      ) : (
        <section className="eng-template-grid">
          {filtered.map((t) => {
            const ch = CHANNEL_META[t.channel] || CHANNEL_META.WhatsApp;
            const linkMeta = LINK_TYPES.find((lt) => lt.id === t.linkType) || LINK_TYPES[3];

            return (
              <article
                className="eng-template-card"
                key={t.id}
                style={{ "--channel-color": ch.color, "--channel-bg": ch.bg }}
              >
                <div className="eng-template-card-top">
                  <span className="eng-template-channel-icon">{ch.icon}</span>
                  <span className="eng-template-channel-pill">{t.channel}</span>
                </div>

                <h3 title={t.name}>{t.name || "Untitled Template"}</h3>

                {t.channel === "Email" && t.subject && (
                  <div className="eng-template-subject" title={t.subject}>
                    Subject: {t.subject}
                  </div>
                )}

                <p>{getTemplateSummary(t) || "No content added yet."}</p>

                <div className="eng-template-card-meta">
                  <span style={{ "--link-color": linkMeta.color, "--link-bg": linkMeta.bg }}>
                    {linkMeta.icon}
                    {linkMeta.label}
                  </span>
                  <span>{getContentLength(t)} chars</span>
                </div>

                {renderCardLinkBadge(t)}

                <div className="eng-template-card-actions">
                  <button type="button" title="Edit template" className="brand" onClick={() => openEdit(t)}>
                    <FaEdit />
                  </button>

                  <button
                    type="button"
                    title="Delete template"
                    className="danger"
                    onClick={() => setConfirmData(t)}
                  >
                    <FaTrash />
                  </button>
                </div>
              </article>
            );
          })}

          <button type="button" className="eng-template-create-card" onClick={openCreate}>
            <span>
              <FaPlus />
            </span>
            <strong>New Template</strong>
            <em>Create a message and link it to a feedback form, offer page, or URL.</em>
          </button>
        </section>
      )}

      {showEditor && editing && (
        <div className="eng-template-drawer-root" role="dialog" aria-modal="true" aria-label="Template editor">
          <button type="button" className="eng-template-drawer-backdrop" onClick={closeEditor} aria-label="Close editor" />

          <div className="eng-template-drawer">
            <header className="eng-template-drawer-head">
              <div className="eng-template-drawer-title">
                <span className="eng-template-drawer-icon">
                  <FaEdit />
                </span>
                <div>
                  <div className="eng-template-eyebrow">Template Editor</div>
                  <h4>{editing.id ? "Edit Template" : "New Template"}</h4>
                  <p>
                    {editing.channel} · {getContentLength(editing)} chars
                  </p>
                </div>
              </div>

              <button type="button" className="eng-template-close-btn" onClick={closeEditor} aria-label="Close">
                <FaTimes />
              </button>
            </header>

            {error && (
              <div className="eng-template-error">
                <FaExclamationTriangle />
                <span>{error}</span>
              </div>
            )}

            <main className="eng-template-drawer-body">
              <div className="eng-template-editor-grid">
                <section className="eng-template-editor-card">
                  <div className="eng-template-section-title">
                    <strong>Template Details</strong>
                    <span>Define name, channel, content, variables, and link destination.</span>
                  </div>

                  <label className="eng-template-field">
                    <span>
                      Template Name <em>*</em>
                    </span>
                    <input
                      type="text"
                      value={editing.name}
                      onChange={(event) => updateEditing("name", event.target.value)}
                      placeholder="e.g. Birthday WhatsApp"
                    />
                  </label>

                  <div className="eng-template-field">
                    <span>Channel</span>
                    <div className="eng-template-channel-selector">
                      {CHANNELS.map((ch) => (
                        <button
                          type="button"
                          key={ch}
                          className={editing.channel === ch ? "is-selected" : ""}
                          onClick={() => handleChannelChange(ch)}
                        >
                          {CHANNEL_META[ch]?.icon}
                          {ch}
                        </button>
                      ))}
                    </div>
                  </div>

                  {editing.channel === "Email" && (
                    <label className="eng-template-field">
                      <span>
                        Email Subject <em>*</em>
                      </span>
                      <input
                        type="text"
                        value={editing.subject}
                        onChange={(event) => updateEditing("subject", event.target.value)}
                        placeholder="e.g. Thank you for staying with us"
                      />
                    </label>
                  )}

                  {editing.channel === "WhatsApp" && (
                    <div className="eng-template-field">
                      <span>Header Image <small>(optional)</small></span>

                      {editing.headerImage ? (
                        <div className="eng-template-image-preview">
                          <img
                            src={editing.headerImage}
                            alt="Header"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                          <div>
                            <span title={editing.headerImage}>{editing.headerImage}</span>
                            <button type="button" onClick={() => updateEditing("headerImage", "")}>
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="eng-template-upload-row">
                          <label className={uploadingImage ? "is-disabled" : ""}>
                            {uploadingImage ? (
                              "Uploading..."
                            ) : (
                              <>
                                <FaUpload /> Upload Image
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              disabled={uploadingImage}
                              onChange={handleUploadImage}
                            />
                          </label>

                          <input
                            type="url"
                            value={editing.headerImage || ""}
                            onChange={(event) => updateEditing("headerImage", event.target.value)}
                            placeholder="or paste CDN URL…"
                          />
                        </div>
                      )}

                      <small>Max 2 MB · JPG, PNG, GIF, WEBP · uploaded to your CDN</small>
                    </div>
                  )}

                  {editing.channel === "Email" ? (
                    <div className="eng-template-field eng-template-email-editor-field">
                      <span>
                        Email Body <em>*</em> <small>HTML supported</small>
                      </span>
                      <EmailHtmlEditor
                        value={editing.content}
                        onChange={(value) => updateEditing("content", value)}
                        placeholder="Design your email body. Use variables like {{guest_name}} and {{link}}."
                      />
                      <small>
                        Use the CTA button or link tool for clickable email calls-to-action. Raw HTML can also be pasted or edited.
                      </small>
                    </div>
                  ) : (
                    <label className="eng-template-field">
                      <span>
                        Message Content <em>*</em>
                      </span>
                      <textarea
                        rows={7}
                        value={editing.content}
                        onChange={(event) => updateEditing("content", event.target.value)}
                        placeholder={`Hi {{guest_name}},\n\nThank you for staying at {{hotel_name}}!\n\nWe'd love your feedback:\n{{link}}\n\nThank you! 🙏`}
                      />
                    </label>
                  )}

                  <div className="eng-template-content-status">
                    <span>{getContentLength(editing)} chars</span>

                    {editing.linkType !== "none" && !hasLinkInMsg && hasTemplateContent(editing.content || "") && (
                      <strong className="danger">
                        <FaExclamationTriangle /> Add {"{{link}}"}
                      </strong>
                    )}

                    {hasLinkInMsg && (
                      <strong className="success">
                        <FaCheckCircle /> {"{{link}}"} ready
                      </strong>
                    )}
                  </div>

                  <div className="eng-template-variable-box">
                    <strong>Insert Variable</strong>
                    <div>
                      {VARIABLES.map(({ v, desc }) => (
                        <button type="button" key={v} title={desc} onClick={() => insertVariable(v)}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="eng-template-link-destination">
                    <div className="eng-template-link-title">
                      <FaLink />
                      <strong>{"{{link}}"} destination</strong>
                    </div>

                    <div className="eng-template-link-types">
                      {LINK_TYPES.map((lt) => (
                        <button
                          type="button"
                          key={lt.id}
                          className={editing.linkType === lt.id ? "is-selected" : ""}
                          style={{ "--link-color": lt.color, "--link-bg": lt.bg || "#f8fafc" }}
                          onClick={() =>
                            setEditing((p) => ({
                              ...p,
                              linkType: lt.id,
                              landingPageId: lt.id !== "landing" ? "" : p.landingPageId,
                              surveyId: lt.id !== "feedback" ? "" : p.surveyId,
                              customUrl: lt.id !== "custom" ? "" : p.customUrl,
                            }))
                          }
                        >
                          <span>{lt.icon}</span>
                          <strong>{lt.label}</strong>
                          <em>{lt.desc}</em>
                        </button>
                      ))}
                    </div>

                    {editing.linkType === "feedback" && (
                      <label className="eng-template-field compact">
                        <span>Select Feedback Survey</span>
                        {surveys.length === 0 ? (
                          <div className="eng-template-warning">No active surveys found.</div>
                        ) : (
                          <select
                            value={editing.surveyId}
                            onChange={(event) => updateEditing("surveyId", event.target.value)}
                          >
                            <option value="">Select survey...</option>
                            {surveys.map((s) => (
                              <option key={s.surveyid} value={s.surveyid}>
                                {s.title}
                              </option>
                            ))}
                          </select>
                        )}
                      </label>
                    )}

                    {editing.linkType === "landing" && (
                      <label className="eng-template-field compact">
                        <span>Select Offer Page</span>
                        {landingPages.length === 0 ? (
                          <div className="eng-template-warning">No offer pages yet.</div>
                        ) : (
                          <select
                            value={editing.landingPageId}
                            onChange={(event) => updateEditing("landingPageId", event.target.value)}
                          >
                            <option value="">Select page...</option>
                            {landingPages.map((lp) => (
                              <option key={lp.id} value={lp.id}>
                                {lp.title}
                              </option>
                            ))}
                          </select>
                        )}
                      </label>
                    )}

                    {editing.linkType === "custom" && (
                      <label className="eng-template-field compact">
                        <span>Custom URL</span>
                        <input
                          type="url"
                          value={editing.customUrl}
                          onChange={(event) => updateEditing("customUrl", event.target.value)}
                          placeholder="https://your-booking-engine.com/reserve"
                        />
                      </label>
                    )}

                    {editing.linkType === "none" && (
                      <div className="eng-template-muted-box">
                        No link selected. Remove <code>{"{{link}}"}</code> or pick a destination type.
                      </div>
                    )}
                  </div>

                  {editing.channel === "WhatsApp" && (
                    <div className="eng-template-warning">
                      WhatsApp templates must be <strong>pre-approved by Meta</strong> via your provider before sending.
                    </div>
                  )}
                </section>

                <section className="eng-template-editor-card sticky">
                  <div className="eng-template-section-title">
                    <strong>
                      <FaEye /> Live Preview
                    </strong>
                    <span>Preview values are sample data for visual testing.</span>
                  </div>

                  <MessagePreview template={editing} link={resolvedLink} />

                  {hasTemplateContent(editing.content || "") && editing.linkType !== "none" && (
                    <div className="eng-template-flow">
                      <strong>Guest Experience Flow</strong>
                      <div>
                        <span style={{ "--flow-bg": CHANNEL_META[editing.channel]?.color || "#4f46e5" }}>
                          Receives {editing.channel}
                        </span>
                        <FaArrowRight />
                        <span style={{ "--flow-bg": "#d97706" }}>Taps link</span>
                        <FaArrowRight />

                        {editing.linkType === "feedback" && editing.surveyId && (
                          <span style={{ "--flow-bg": "#059669" }}>
                            {surveys.find((s) => String(s.surveyid) === String(editing.surveyId))?.title || "Feedback Form"}
                          </span>
                        )}

                        {editing.linkType === "landing" && editing.landingPageId && (
                          <span style={{ "--flow-bg": "#4f46e5" }}>
                            {landingPages.find((lp) => String(lp.id) === String(editing.landingPageId))?.title || "Offer Page"}
                          </span>
                        )}

                        {editing.linkType === "custom" && editing.customUrl && (
                          <span style={{ "--flow-bg": "#6b7280" }}>Custom URL</span>
                        )}

                        <FaArrowRight />
                        <span style={{ "--flow-bg": "#111827" }}>Tracked</span>
                      </div>
                    </div>
                  )}

                  <div className="eng-template-preview-values">
                    <strong>Preview values</strong>
                    <span><code>{"{{guest_name}}"}</code> → Rahul Sharma</span>
                    <span><code>{"{{hotel_name}}"}</code> → Grand Netshine Hotel</span>
                    <span>
                      <code>{"{{link}}"}</code> →{" "}
                      {resolvedLink ? `${resolvedLink.url?.substring(0, 46)}…` : "no link set"}
                    </span>
                  </div>
                </section>
              </div>
            </main>

            <footer className="eng-template-drawer-footer">
              <button type="button" className="eng-template-soft-btn" onClick={closeEditor} disabled={saving || uploadingImage}>
                Cancel
              </button>

              <button type="button" className="eng-template-primary-btn" onClick={save} disabled={saving || uploadingImage}>
                <FaSave />
                {saving ? "Saving..." : "Save Template"}
              </button>
            </footer>
          </div>
        </div>
      )}

      {confirmData && (
        <div className="eng-template-delete-root" role="dialog" aria-modal="true" aria-label="Delete template">
          <button
            type="button"
            className="eng-template-delete-backdrop"
            onClick={() => setConfirmData(null)}
            aria-label="Cancel delete"
          />

          <div className="eng-template-delete-card">
            <div className="eng-template-delete-icon">
              <FaTrash />
            </div>

            <div className="eng-template-delete-copy">
              <strong>Delete Template?</strong>
              <p>
                <b>{confirmData?.name}</b> will be permanently deleted. Campaigns or journeys using this template may
                need to be reviewed.
              </p>
            </div>

            <div className="eng-template-delete-actions">
              <button type="button" className="eng-template-soft-btn" onClick={() => setConfirmData(null)}>
                <FaTimes />
                Cancel
              </button>

              <button type="button" className="eng-template-danger-btn" onClick={deleteTemplate}>
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const templateStyles = `
  body.eng-template-modal-open {
    overflow: hidden !important;
  }

  .eng-template-root {
    min-height: 100%;
    padding: 18px 22px 28px;
    background:
      radial-gradient(circle at 7% 0%, rgba(79, 70, 229, 0.08), transparent 28%),
      radial-gradient(circle at 92% 3%, rgba(5, 150, 105, 0.08), transparent 30%),
      #f8fafc;
    color: #111827;
  }

  .eng-template-hero {
    background:
      radial-gradient(circle at top left, rgba(79, 70, 229, 0.14), transparent 34%),
      radial-gradient(circle at top right, rgba(5, 150, 105, 0.10), transparent 32%),
      linear-gradient(135deg, #ffffff 0%, #fbfdff 100%);
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 24px;
    box-shadow: 0 16px 42px rgba(17, 24, 39, 0.075);
    padding: 17px 18px;
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
  }

  .eng-template-hero-left {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    min-width: 0;
  }

  .eng-template-hero-icon,
  .eng-template-empty-icon {
    width: 52px;
    height: 52px;
    border-radius: 19px;
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 34px rgba(79, 70, 229, 0.22);
    flex: 0 0 auto;
    font-size: 1.25rem;
  }

  .eng-template-eyebrow {
    color: #4f46e5;
    font-size: 0.72rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-weight: 950;
    margin-bottom: 5px;
  }

  .eng-template-hero h2 {
    color: #111827;
    font-size: 1.42rem;
    line-height: 1.16;
    font-weight: 950;
    margin: 0;
  }

  .eng-template-hero p {
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.5;
    font-weight: 760;
    margin: 7px 0 0;
    max-width: 790px;
  }

  .eng-template-hero-actions,
  .eng-template-filter-group,
  .eng-template-card-actions,
  .eng-template-delete-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .eng-template-hero-actions {
    justify-content: flex-end;
  }

  .eng-template-primary-btn,
  .eng-template-soft-btn,
  .eng-template-danger-btn {
    min-height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 15px;
    font-size: 0.82rem;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
    transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
  }

  .eng-template-primary-btn {
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    border-color: #4f46e5;
    color: #ffffff;
    box-shadow: 0 14px 30px rgba(79, 70, 229, 0.18);
  }

  .eng-template-soft-btn {
    background: #ffffff;
    color: #334155;
  }

  .eng-template-danger-btn {
    background: linear-gradient(135deg, #dc2626, #ef4444);
    border-color: #dc2626;
    color: #ffffff;
    box-shadow: 0 14px 30px rgba(220, 38, 38, 0.18);
  }

  .eng-template-primary-btn:hover,
  .eng-template-soft-btn:hover,
  .eng-template-danger-btn:hover {
    transform: translateY(-1px);
  }

  .eng-template-primary-btn:disabled,
  .eng-template-soft-btn:disabled {
    opacity: 0.62;
    cursor: not-allowed;
    transform: none;
  }

  .eng-spin {
    animation: eng-spin 0.9s linear infinite;
  }

  @keyframes eng-spin {
    to { transform: rotate(360deg); }
  }

  .eng-template-alert {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #bbf7d0;
    border-radius: 18px;
    padding: 11px 13px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 0.82rem;
    font-weight: 850;
  }

  .eng-template-alert button {
    margin-left: auto;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }

  .eng-template-stat-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }

  .eng-template-stat {
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-top: 4px solid var(--tone);
    border-radius: 20px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    padding: 13px 14px;
    min-width: 0;
  }

  .eng-template-stat span {
    color: #64748b;
    display: block;
    font-size: 0.66rem;
    font-weight: 950;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 7px;
  }

  .eng-template-stat strong {
    color: #0f172a;
    display: block;
    font-size: 1.22rem;
    line-height: 1.12;
    font-weight: 950;
  }

  .eng-template-tip,
  .eng-template-toolbar {
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 22px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    margin-bottom: 12px;
  }

  .eng-template-tip {
    padding: 12px 14px;
    color: #64748b;
    font-size: 0.8rem;
    font-weight: 760;
    display: flex;
    align-items: flex-start;
    gap: 9px;
  }

  .eng-template-tip svg {
    color: #4f46e5;
    margin-top: 2px;
    flex: 0 0 auto;
  }

  .eng-template-tip code {
    color: #4f46e5;
    font-weight: 950;
  }

  .eng-template-toolbar {
    padding: 12px;
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto;
    gap: 10px;
    align-items: center;
  }

  .eng-template-search {
    min-height: 42px;
    border-radius: 15px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #ffffff;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 0 12px;
  }

  .eng-template-search svg {
    color: #94a3b8;
    flex: 0 0 auto;
  }

  .eng-template-search input {
    width: 100%;
    min-height: 40px;
    border: 0;
    outline: none;
    color: #0f172a;
    font-size: 0.84rem;
    font-weight: 760;
  }

  .eng-template-filter-group {
    justify-content: flex-end;
  }

  .eng-template-filter-label {
    min-height: 38px;
    border-radius: 999px;
    background: #f8fafc;
    color: #64748b;
    border: 1px solid rgba(17, 24, 39, 0.08);
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 11px;
    font-size: 0.74rem;
    font-weight: 950;
  }

  .eng-template-filter-group select {
    min-height: 40px;
    min-width: 142px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #ffffff;
    color: #0f172a;
    padding: 0 10px;
    font-size: 0.82rem;
    font-weight: 850;
    outline: none;
  }

  .eng-template-clear-btn {
    min-height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #fff;
    color: #4f46e5;
    padding: 0 12px;
    font-size: 0.78rem;
    font-weight: 950;
    cursor: pointer;
  }

  .eng-template-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(286px, 1fr));
    gap: 12px;
  }

  .eng-template-card,
  .eng-template-create-card {
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-top: 4px solid var(--channel-color, #4f46e5);
    border-radius: 22px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    padding: 14px;
    min-height: 250px;
    display: flex;
    flex-direction: column;
    gap: 11px;
    transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
  }

  .eng-template-card:hover,
  .eng-template-create-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 42px rgba(17, 24, 39, 0.11);
  }

  .eng-template-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  .eng-template-channel-icon {
    width: 44px;
    height: 44px;
    border-radius: 16px;
    background: var(--channel-bg);
    color: var(--channel-color);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }

  .eng-template-channel-pill {
    min-height: 28px;
    border-radius: 999px;
    background: var(--channel-bg);
    color: var(--channel-color);
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    font-size: 0.68rem;
    font-weight: 950;
    white-space: nowrap;
  }

  .eng-template-card h3 {
    color: #111827;
    font-size: 0.96rem;
    line-height: 1.28;
    font-weight: 950;
    margin: 0;
    min-height: 38px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .eng-template-subject {
    color: #4f46e5;
    background: #eef2ff;
    border: 1px solid #c7d2fe;
    border-radius: 12px;
    padding: 7px 9px;
    font-size: 0.72rem;
    font-weight: 850;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .eng-template-card p {
    color: #64748b;
    font-size: 0.8rem;
    line-height: 1.52;
    font-weight: 760;
    margin: 0;
    min-height: 56px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .eng-template-card-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: #64748b;
    font-size: 0.68rem;
    font-weight: 850;
    margin-top: auto;
  }

  .eng-template-card-meta span:first-child {
    min-height: 24px;
    border-radius: 999px;
    background: var(--link-bg);
    color: var(--link-color);
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px;
    font-size: 0.68rem;
    font-weight: 950;
  }

  .eng-template-link-badge {
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    padding: 9px;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    font-size: 0.74rem;
  }

  .eng-template-link-badge svg {
    flex: 0 0 auto;
  }

  .eng-template-link-badge strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .eng-template-link-badge em {
    display: block;
    color: #64748b;
    font-style: normal;
    margin-top: 2px;
  }

  .eng-template-link-badge.feedback { background: #f0fdf4; border-color: #bbf7d0; color: #15803d; }
  .eng-template-link-badge.landing { background: #eef2ff; border-color: #c7d2fe; color: #4f46e5; }
  .eng-template-link-badge.custom { background: #f8fafc; border-color: #e2e8f0; color: #475569; }
  .eng-template-link-badge.warning { background: #fff7ed; border-color: #fed7aa; color: #d97706; }

  .eng-template-link-badge button {
    border: 0;
    background: transparent;
    color: inherit;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
  }

  .eng-template-card-actions {
    justify-content: flex-end;
    margin-top: 2px;
  }

  .eng-template-card-actions button {
    flex: 1;
    min-height: 36px;
    border-radius: 12px;
    border: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .eng-template-card-actions button.brand { background: #eef2ff; color: #4f46e5; }
  .eng-template-card-actions button.danger { background: #fef2f2; color: #ef4444; }

  .eng-template-create-card {
    border-top-color: #4f46e5;
    border-style: dashed;
    align-items: center;
    justify-content: center;
    text-align: center;
    cursor: pointer;
    color: #4f46e5;
  }

  .eng-template-create-card span {
    width: 52px;
    height: 52px;
    border-radius: 19px;
    background: #eef2ff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
  }

  .eng-template-create-card strong {
    color: #111827;
    font-size: 0.98rem;
    font-weight: 950;
  }

  .eng-template-create-card em {
    color: #64748b;
    font-size: 0.78rem;
    line-height: 1.45;
    font-style: normal;
    max-width: 240px;
  }

  .eng-template-empty,
  .eng-template-loader {
    min-height: 360px;
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    border-radius: 24px;
    box-shadow: 0 16px 42px rgba(17, 24, 39, 0.06);
    padding: 40px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-align: center;
  }

  .eng-template-empty strong,
  .eng-template-loader strong {
    color: #111827;
    font-size: 1.02rem;
    font-weight: 950;
  }

  .eng-template-empty p,
  .eng-template-loader p {
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.48;
    font-weight: 760;
    max-width: 430px;
    margin: 0 0 8px;
  }

  .eng-template-dots {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
  }

  .eng-template-dots span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #4f46e5;
    animation: eng-template-bounce 0.58s infinite alternate;
  }

  .eng-template-dots span:nth-child(2) { animation-delay: 0.1s; }
  .eng-template-dots span:nth-child(3) { animation-delay: 0.2s; }
  .eng-template-dots span:nth-child(4) { animation-delay: 0.3s; }

  @keyframes eng-template-bounce {
    to { transform: translateY(-10px); }
  }

  .eng-template-drawer-root,
  .eng-template-delete-root {
    position: fixed;
    inset: 0;
    z-index: 2147483000;
    background: rgba(15, 23, 42, 0.10);
    backdrop-filter: blur(1.5px);
    -webkit-backdrop-filter: blur(1.5px);
  }

  .eng-template-drawer-root {
    display: flex;
    justify-content: flex-end;
  }

  .eng-template-drawer-backdrop,
  .eng-template-delete-backdrop {
    position: absolute;
    inset: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
  }

  .eng-template-drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(980px, 100vw);
    height: 100vh;
    height: 100dvh;
    z-index: 2147483001;
    background:
      radial-gradient(circle at top left, rgba(79, 70, 229, 0.12), transparent 34%),
      #f8fafc;
    box-shadow: -18px 0 52px rgba(15, 23, 42, 0.16);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .eng-template-drawer-head {
    flex: 0 0 auto;
    background:
      radial-gradient(circle at top left, rgba(79, 70, 229, 0.14), transparent 36%),
      linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    padding: 18px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
  }

  .eng-template-drawer-title {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
  }

  .eng-template-drawer-icon {
    width: 48px;
    height: 48px;
    border-radius: 18px;
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 14px 30px rgba(79, 70, 229, 0.22);
    flex: 0 0 auto;
  }

  .eng-template-drawer-head h4 {
    color: #111827;
    font-size: 1.24rem;
    line-height: 1.14;
    font-weight: 950;
    margin: 0;
  }

  .eng-template-drawer-head p {
    color: #64748b;
    font-size: 0.78rem;
    line-height: 1.42;
    font-weight: 760;
    margin: 5px 0 0;
  }

  .eng-template-close-btn {
    width: 40px;
    height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(220, 38, 38, 0.16);
    background: #fff1f2;
    color: #dc2626;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .eng-template-error {
    flex: 0 0 auto;
    background: #fef2f2;
    color: #dc2626;
    border-bottom: 1px solid #fecaca;
    padding: 10px 18px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.82rem;
    font-weight: 850;
  }

  .eng-template-drawer-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 15px 18px 112px;
    scrollbar-width: thin;
    scrollbar-color: #a5b4fc #e2e8f0;
  }

  .eng-template-editor-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.05fr) minmax(360px, 0.95fr);
    gap: 14px;
    align-items: start;
  }

  .eng-template-editor-card {
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 22px;
    box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
    padding: 15px;
  }

  .eng-template-editor-card.sticky {
    position: sticky;
    top: 0;
  }

  .eng-template-section-title {
    margin-bottom: 14px;
  }

  .eng-template-section-title strong {
    color: #111827;
    font-size: 0.96rem;
    font-weight: 950;
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .eng-template-section-title span {
    color: #64748b;
    font-size: 0.74rem;
    line-height: 1.42;
    font-weight: 760;
    display: block;
    margin-top: 3px;
  }

  .eng-template-field {
    display: grid;
    gap: 7px;
    margin-bottom: 13px;
  }

  .eng-template-field.compact {
    margin-top: 12px;
    margin-bottom: 0;
  }

  .eng-template-field > span {
    color: #0f172a;
    font-size: 0.77rem;
    font-weight: 950;
  }

  .eng-template-field > span em {
    color: #dc2626;
    font-style: normal;
    margin-left: 2px;
  }

  .eng-template-field > span small {
    color: #94a3b8;
    font-weight: 760;
    margin-left: 4px;
  }

  .eng-template-field input,
  .eng-template-field textarea,
  .eng-template-field select,
  .eng-template-upload-row input {
    width: 100%;
    min-height: 42px;
    border-radius: 14px;
    border: 1px solid rgba(17, 24, 39, 0.12);
    background: #ffffff;
    color: #111827;
    font-size: 0.86rem;
    font-weight: 760;
    outline: none;
    padding: 0 12px;
  }

  .eng-template-field textarea {
    min-height: 146px;
    padding: 11px 12px;
    line-height: 1.52;
    resize: vertical;
  }

  .eng-template-field input:focus,
  .eng-template-field textarea:focus,
  .eng-template-field select:focus,
  .eng-template-upload-row input:focus {
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
  }

  .eng-template-channel-selector,
  .eng-template-upload-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .eng-template-channel-selector button {
    min-height: 36px;
    border-radius: 999px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #ffffff;
    color: #334155;
    padding: 0 13px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.78rem;
    font-weight: 900;
    cursor: pointer;
  }

  .eng-template-channel-selector button.is-selected {
    background: #eef2ff;
    color: #4f46e5;
    border-color: #c7d2fe;
  }

  .eng-template-image-preview {
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid rgba(17, 24, 39, 0.08);
  }

  .eng-template-image-preview img {
    width: 100%;
    max-height: 160px;
    object-fit: cover;
    display: block;
  }

  .eng-template-image-preview div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    background: #f8fafc;
    border-top: 1px solid #e5e7eb;
  }

  .eng-template-image-preview span {
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 760;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .eng-template-image-preview button {
    border: 0;
    border-radius: 10px;
    background: #fee2e2;
    color: #dc2626;
    font-size: 0.72rem;
    font-weight: 950;
    padding: 5px 10px;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .eng-template-upload-row {
    align-items: stretch;
  }

  .eng-template-upload-row label {
    min-height: 42px;
    border-radius: 14px;
    border: 1px solid #c7d2fe;
    background: #eef2ff;
    color: #4f46e5;
    padding: 0 13px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.8rem;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
  }

  .eng-template-upload-row label.is-disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }

  .eng-template-upload-row label input {
    display: none;
  }

  .eng-template-upload-row > input {
    flex: 1 1 220px;
  }


  .eng-template-email-editor-field > small {
    color: #64748b;
    font-size: 0.72rem;
    line-height: 1.45;
    font-weight: 760;
  }

  .eng-template-html-editor {
    border: 1px solid rgba(17, 24, 39, 0.12);
    border-radius: 16px;
    background: #ffffff;
    overflow: hidden;
  }

  .eng-template-html-toolbar {
    min-height: 42px;
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: wrap;
    padding: 7px;
    background: #f8fafc;
    border-bottom: 1px solid rgba(17, 24, 39, 0.08);
  }

  .eng-template-html-toolbar button {
    min-height: 31px;
    border-radius: 10px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #ffffff;
    color: #334155;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 9px;
    font-size: 0.72rem;
    font-weight: 950;
    cursor: pointer;
  }

  .eng-template-html-toolbar button:hover,
  .eng-template-html-toolbar button.is-active {
    background: #eef2ff;
    color: #4f46e5;
    border-color: #c7d2fe;
  }

  .eng-template-html-surface {
    min-height: 220px;
    max-height: 420px;
    overflow-y: auto;
    padding: 14px;
    color: #111827;
    font-size: 0.9rem;
    line-height: 1.58;
    font-weight: 650;
    outline: none;
  }

  .eng-template-html-surface:focus {
    box-shadow: inset 0 0 0 3px rgba(79, 70, 229, 0.10);
  }

  .eng-template-html-surface:empty::before {
    content: attr(data-placeholder);
    color: #94a3b8;
    font-style: italic;
    pointer-events: none;
  }

  .eng-template-html-surface h1,
  .eng-template-html-surface h2,
  .eng-template-html-surface h3 {
    margin: 0 0 10px;
    color: #111827;
    line-height: 1.2;
  }

  .eng-template-html-surface p {
    margin: 0 0 10px;
  }

  .eng-template-html-surface a {
    color: #4f46e5;
    font-weight: 850;
  }

  .eng-template-html-surface blockquote {
    margin: 10px 0;
    padding: 10px 12px;
    border-left: 4px solid #c7d2fe;
    background: #f8fafc;
    color: #475569;
  }

  .eng-template-html-source {
    width: 100%;
    min-height: 260px;
    border: 0;
    outline: none;
    resize: vertical;
    padding: 13px;
    color: #111827;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
    font-size: 0.78rem;
    line-height: 1.55;
  }

  .eng-template-content-status {
    min-height: 24px;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
    color: #64748b;
    font-size: 0.74rem;
    font-weight: 850;
    margin: -6px 0 13px;
  }

  .eng-template-content-status strong {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.74rem;
  }

  .eng-template-content-status strong.danger { color: #dc2626; }
  .eng-template-content-status strong.success { color: #059669; }

  .eng-template-variable-box,
  .eng-template-link-destination,
  .eng-template-flow,
  .eng-template-preview-values {
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 18px;
    background: #f8fafc;
    padding: 13px;
    margin-bottom: 13px;
  }

  .eng-template-variable-box strong,
  .eng-template-flow strong,
  .eng-template-preview-values strong {
    color: #111827;
    display: block;
    font-size: 0.82rem;
    font-weight: 950;
    margin-bottom: 8px;
  }

  .eng-template-variable-box div {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .eng-template-variable-box button {
    min-height: 28px;
    border-radius: 999px;
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #334155;
    padding: 0 9px;
    font-size: 0.72rem;
    font-weight: 850;
    cursor: pointer;
  }

  .eng-template-link-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4f46e5;
    margin-bottom: 12px;
  }

  .eng-template-link-types {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
  }

  .eng-template-link-types button {
    min-height: 112px;
    border-radius: 16px;
    border: 1px solid rgba(17, 24, 39, 0.10);
    background: #ffffff;
    color: var(--link-color);
    padding: 11px;
    text-align: left;
    display: grid;
    gap: 5px;
    cursor: pointer;
  }

  .eng-template-link-types button.is-selected {
    border-color: var(--link-color);
    background: var(--link-bg);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--link-color) 10%, transparent);
  }

  .eng-template-link-types button span {
    font-size: 1rem;
  }

  .eng-template-link-types button strong {
    color: var(--link-color);
    font-size: 0.8rem;
    font-weight: 950;
  }

  .eng-template-link-types button em {
    color: #64748b;
    font-size: 0.68rem;
    line-height: 1.35;
    font-style: normal;
  }

  .eng-template-warning,
  .eng-template-muted-box {
    border-radius: 14px;
    padding: 10px 12px;
    font-size: 0.78rem;
    line-height: 1.42;
    font-weight: 800;
  }

  .eng-template-warning {
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }

  .eng-template-muted-box {
    color: #64748b;
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    text-align: center;
  }

  .eng-template-preview-whatsapp {
    min-height: 180px;
    border-radius: 20px;
    background: #e5ddd5;
    padding: 14px;
    border: 1px solid rgba(17, 24, 39, 0.08);
  }

  .eng-template-preview-empty {
    min-height: 150px;
    color: #64748b;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
  }

  .eng-template-preview-empty svg {
    color: #94a3b8;
    font-size: 1.4rem;
  }

  .eng-template-whatsapp-bubble {
    margin-left: auto;
    background: #dcf8c6;
    max-width: 92%;
    border-radius: 16px 4px 16px 16px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
  }

  .eng-template-whatsapp-image {
    width: 100%;
    max-height: 160px;
    object-fit: cover;
    display: block;
  }

  .eng-template-preview-text {
    color: #111827;
    font-size: 0.84rem;
    line-height: 1.6;
    white-space: pre-wrap;
    padding: 13px;
  }

  .eng-template-preview-link {
    margin: 0 13px 9px;
    padding-top: 9px;
    border-top: 1px solid rgba(5, 150, 105, 0.32);
    color: #0d6efd;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.74rem;
    flex-wrap: wrap;
  }

  .eng-template-preview-link em {
    color: #64748b;
    font-style: normal;
  }

  .eng-template-preview-time {
    color: #64748b;
    text-align: right;
    font-size: 0.68rem;
    padding: 0 11px 8px;
  }

  .eng-template-preview-sms,
  .eng-template-preview-email {
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 20px;
    overflow: hidden;
    background: #ffffff;
  }

  .eng-template-preview-sms {
    background: #f8fafc;
    padding: 3px;
  }

  .eng-template-preview-sms .eng-template-preview-text {
    padding: 13px;
  }

  .eng-template-sms-link {
    color: #0d6efd;
    font-size: 0.75rem;
    padding: 0 13px 13px;
    font-weight: 850;
  }

  .eng-template-email-head {
    background: #f8fafc;
    border-bottom: 1px solid rgba(17, 24, 39, 0.08);
    padding: 10px 13px;
    display: grid;
    gap: 4px;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 760;
  }

  .eng-template-email-head strong {
    color: #111827;
    font-size: 0.82rem;
    font-weight: 950;
  }

  .eng-template-email-body {
    min-height: 130px;
  }


  .eng-template-email-html {
    min-height: 130px;
    padding: 16px;
    color: #1f2937;
    font-size: 0.86rem;
    line-height: 1.6;
    overflow-wrap: anywhere;
  }

  .eng-template-email-html h1,
  .eng-template-email-html h2,
  .eng-template-email-html h3,
  .eng-template-email-html h4 {
    margin: 0 0 10px;
    color: #111827;
    line-height: 1.22;
  }

  .eng-template-email-html p {
    margin: 0 0 11px;
  }

  .eng-template-email-html a {
    color: #0d6efd;
    font-weight: 850;
  }

  .eng-template-email-html img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
  }

  .eng-template-email-html blockquote {
    margin: 10px 0;
    padding: 10px 12px;
    border-left: 4px solid #c7d2fe;
    background: #f8fafc;
    color: #475569;
  }

  .eng-template-email-html table {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0;
  }

  .eng-template-email-html td,
  .eng-template-email-html th {
    border: 1px solid #e5e7eb;
    padding: 8px;
    text-align: left;
  }

  .eng-template-email-link {
    color: #0d6efd;
    font-size: 0.78rem;
    font-weight: 850;
    padding: 0 13px 13px;
  }

  .eng-template-muted {
    color: #94a3b8;
    font-size: 0.82rem;
    font-style: italic;
    display: block;
    padding: 13px;
  }

  .eng-template-flow {
    margin-top: 14px;
    margin-bottom: 13px;
  }

  .eng-template-flow div {
    display: flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
    color: #64748b;
    font-size: 0.74rem;
  }

  .eng-template-flow span {
    min-height: 26px;
    border-radius: 999px;
    background: var(--flow-bg);
    color: #ffffff;
    padding: 4px 9px;
    display: inline-flex;
    align-items: center;
    font-size: 0.7rem;
    font-weight: 900;
  }

  .eng-template-preview-values {
    display: grid;
    gap: 5px;
    color: #64748b;
    font-size: 0.74rem;
    font-weight: 760;
  }

  .eng-template-preview-values code {
    color: #4f46e5;
    font-weight: 950;
  }

  .eng-template-drawer-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.96);
    border-top: 1px solid rgba(15, 23, 42, 0.08);
    box-shadow: 0 -14px 34px rgba(15, 23, 42, 0.08);
    padding: 13px 18px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    z-index: 4;
  }

  .eng-template-delete-root {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
  }

  .eng-template-delete-card {
    position: relative;
    width: min(440px, 100%);
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 24px;
    box-shadow: 0 24px 70px rgba(17, 24, 39, 0.22);
    padding: 22px;
    display: grid;
    gap: 14px;
    z-index: 2147483001;
  }

  .eng-template-delete-icon {
    width: 54px;
    height: 54px;
    border-radius: 20px;
    background: #fef2f2;
    color: #ef4444;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
  }

  .eng-template-delete-copy strong {
    color: #111827;
    display: block;
    font-size: 1.08rem;
    font-weight: 950;
  }

  .eng-template-delete-copy p {
    color: #64748b;
    font-size: 0.82rem;
    line-height: 1.48;
    font-weight: 760;
    margin: 6px 0 0;
  }

  .eng-template-delete-actions {
    justify-content: flex-end;
  }

  @media (max-width: 1320px) {
    .eng-template-stat-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .eng-template-toolbar,
    .eng-template-editor-grid {
      grid-template-columns: 1fr;
    }

    .eng-template-filter-group {
      justify-content: flex-start;
    }

    .eng-template-editor-card.sticky {
      position: static;
    }
  }

  @media (max-width: 720px) {
    .eng-template-root {
      padding: 12px 10px 22px;
    }

    .eng-template-hero-actions,
    .eng-template-filter-group {
      width: 100%;
    }

    .eng-template-primary-btn,
    .eng-template-soft-btn,
    .eng-template-danger-btn,
    .eng-template-filter-group select,
    .eng-template-clear-btn {
      width: 100%;
    }

    .eng-template-stat-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .eng-template-link-types {
      grid-template-columns: 1fr;
    }

    .eng-template-drawer-footer,
    .eng-template-delete-actions {
      display: grid;
      grid-template-columns: 1fr;
    }

    .eng-template-drawer-body {
      padding-left: 12px;
      padding-right: 12px;
    }
  }

  @media (max-width: 520px) {
    .eng-template-stat-grid,
    .eng-template-grid {
      grid-template-columns: 1fr;
    }

    .eng-template-whatsapp-bubble {
      max-width: 100%;
    }
  }
`;

export default TemplateManager;
